import React, { useEffect, useState } from 'react';
import { FormField } from './FormField';
import { PlusIcon, TrashIcon, InfoIcon } from 'lucide-react';
export interface MetadataItem {
  key: string;
  value: string;
  isPreset?: boolean;
  required?: boolean;
  type?: string;
  description?: string;
}
interface MetadataEditorProps {
  metadata: MetadataItem[];
  onChange: (metadata: MetadataItem[]) => void;
}
export function MetadataEditor({
  metadata,
  onChange
}: MetadataEditorProps) {
  const [showPresetInfo, setShowPresetInfo] = useState(false);
  // Get preset fields from "settings" - in a real app this would come from localStorage or API
  // For this demo, we'll hardcode some preset fields
  const [presetFields] = useState([{
    key: 'customer_email',
    value: '',
    isPreset: true,
    required: true,
    type: 'email',
    description: "Customer's email address for receipts and communication"
  }, {
    key: 'invoice_number',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Reference invoice number for this payment'
  }]);
  // Add preset fields if they're not already in the metadata
  useEffect(() => {
    const existingKeys = metadata.map(item => item.key);
    const missingPresets = presetFields.filter(preset => !existingKeys.includes(preset.key));
    if (missingPresets.length > 0) {
      onChange([...metadata, ...missingPresets]);
    }
  }, []);
  const getFieldType = (type: string | undefined) => {
    switch (type) {
      case 'email':
        return 'email';
      case 'number':
        return 'number';
      case 'phone':
        return 'tel';
      case 'amount':
        return 'number';
      case 'date':
        return 'date';
      case 'url':
        return 'url';
      default:
        return 'text';
    }
  };
  return <FormField label="Custom metadata" tooltip="Add additional data to track with this payment" data-id="element-1158">
      {/* Preset fields info */}
      {metadata.some(item => item.isPreset) && <div className="mb-3" data-id="element-1159">
          <button type="button" onClick={() => setShowPresetInfo(!showPresetInfo)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-1160">
            <InfoIcon size={14} className="mr-1" data-id="element-1161" />
            {showPresetInfo ? 'Hide preset fields info' : 'About preset fields'}
          </button>
          {showPresetInfo && <div className="mt-2 p-3 bg-blue-50 border border-blue-100 rounded-md text-sm text-blue-800" data-id="element-1162">
              <p data-id="element-1163">
                Preset fields are configured in your settings and automatically
                included in all payment links.
              </p>
              <p className="mt-1" data-id="element-1164">
                Required preset fields cannot be removed. You can modify their
                values but not their keys.
              </p>
            </div>}
        </div>}
      <div className="mt-2" data-id="element-1165">
        <button type="button" onClick={() => onChange([...metadata, {
        key: '',
        value: ''
      }])} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-1166">
          <PlusIcon size={16} className="mr-1" data-id="element-1167" />
          Add custom field
        </button>
      </div>
      {metadata.length > 0 && <div className="mt-3 space-y-3" data-id="element-1168">
          {metadata.map((item, index) => <div key={index} className="flex space-x-2" data-id="element-1169">
              <div className="w-1/2" data-id="element-1170">
                <input type="text" className={`block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 ${item.isPreset ? 'bg-gray-50' : ''}`} placeholder="Key" value={item.key} onChange={e => {
            if (item.isPreset) return; // Prevent editing preset keys
            const updatedMetadata = [...metadata];
            updatedMetadata[index].key = e.target.value;
            onChange(updatedMetadata);
          }} readOnly={item.isPreset} data-id="element-1171" />
                {item.description && <div className="mt-1 text-xs text-gray-500" data-id="element-1172">
                    {item.description}
                  </div>}
              </div>
              <div className="w-1/2" data-id="element-1173">
                <input type={getFieldType(item.type)} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={item.isPreset ? `Enter ${item.key}` : 'Value'} value={item.value} onChange={e => {
            const updatedMetadata = [...metadata];
            updatedMetadata[index].value = e.target.value;
            onChange(updatedMetadata);
          }} data-id="element-1174" />
                {item.required && <div className="mt-1 text-xs text-red-500" data-id="element-1175">Required</div>}
              </div>
              <button type="button" onClick={() => {
          // Only allow removing non-preset fields or optional preset fields
          if (item.isPreset && item.required) return;
          const updatedMetadata = [...metadata];
          updatedMetadata.splice(index, 1);
          onChange(updatedMetadata);
        }} className={`p-2 ${item.isPreset && item.required ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-red-500'}`} disabled={item.isPreset && item.required} data-id="element-1176">
                <TrashIcon size={18} data-id="element-1177" />
              </button>
            </div>)}
        </div>}
    </FormField>;
}